import { z } from 'zod'
import { assignmentTypes } from '../../../assignmentTypes.js'
import { hasColumnDates, memoCandidateColumn } from './columns.js'

/**
 * Which index document types a memo template draws its candidate items from,
 * and which newsvalues each of them is listed at.
 *
 * The newsvalues are the string values used in `@/defaults/newsvalues`, indexed
 * at `document.meta.core_newsvalue.value`. They are the initial value of the
 * dialog's own per column newsvalue filter, which is a query condition - so
 * they decide what the columns open showing, and the editor widens or narrows
 * it from there. An empty list means no newsvalue condition at all for that
 * source. A print article carries no newsvalue, so the list is meaningless for
 * one and its column is left unconditioned either way.
 *
 * Nothing they name starts out checked: what goes in the memo is the editor's
 * to pick, item by item, out of what the columns list.
 *
 * `flow` names the print flow a print-article source is restricted to, by its
 * title, e.g. "Nyheter" - the print desk runs several and a Print-PM is written
 * about one of them. It is a query condition rather than a default, and means
 * nothing for any other source type.
 *
 * `leadDays` is the template's own `leadDays` for this source's column alone, for
 * a template whose columns are about different days - a Print-PM lists
 * tomorrow's paper beside today's articles. Declaring it on any source gives
 * every column a date range of its own in the dialog. Titles still follow the
 * template's lead.
 *
 * `sections` is the section filter the source's column opens with, by title
 * since section uuids differ between environments. Like the newsvalues it is a
 * default the editor changes from there; an empty list filters on no section.
 *
 * A candidate is mapped into the template's default block regardless of its
 * source - which block it ends up rendered under is for the editor to arrange,
 * not something the source type or newsvalue predetermines. See
 * `defaultBlockId` below.
 */
const memoSourceTypeSchema = z.enum(['planning', 'article', 'event', 'print-article'])
const memoNewsvalueSchema = z.enum(['1', '2', '3', '4', '5', '6'])

export const memoSourceSchema = z.object({
  type: memoSourceTypeSchema,
  newsvalues: z.array(memoNewsvalueSchema).default([]),
  sections: z.array(z.string().min(1)).default([]),
  flow: z.string().min(1).optional(),
  leadDays: z.number().int().nonnegative().optional()
})

/**
 * How a memo written for one section names itself, for a template whose title
 * and slugline both read differently when it is.
 *
 * E.g. "TT:s utrikes vecko-pm den 14-20 september 2026" sluglined
 * "TT-utrikes-vecko-pm" when narrowed to Utrikes, and a plain "TT:s vecko-pm den
 * 14-20 september 2026" sluglined "vecko-pm" across the desk - two namings
 * rather than one with a hole in it. No shipped template names itself this way.
 *
 * Used only where the memo is actually written for a section, which is not the
 * same as the section it is filed in - see `writtenForSection` in the creation
 * dialog. Without it a template names itself the one way whatever it covers.
 *
 * `{sectionTitle}` is the section as written and `{sectionLower}` the same in
 * lower case, for a title that names its section mid-sentence. `{sectionInitial}`
 * is its first letter and `{sectionSlug}` the whole of it slugified, both lower
 * case, sluglines being written that way whatever case the datastore holds the
 * section in. Every date placeholder resolves here too.
 */
export const memoSectionNamingSchema = z.object({
  titlePattern: z.string().min(1),
  slugline: z.string().min(1)
})

/**
 * How the new planning item that carries the memo is filled in.
 *
 * `titlePattern` supports the placeholders `resolveMemoTitle` resolves:
 * `{weekday}`, `{dayName}`, `{date}`, `{fromDate}`, `{toDate}`, `{dateRange}`,
 * `{shortDateRange}`, `{year}`, `{week}`, `{sectionTitle}`, `{sectionLower}`,
 * `{sectionInitial}` and `{sectionSlug}`.
 * The newsvalue is deliberately not a template setting - see
 * `MEMO_NEWSVALUE` in `./planning.js` - and neither is the section, unless the
 * template asks the editor for one through `sectionChoice`.
 *
 * `section` is how the same memo names itself when it is written for one
 * section, and is what the two above are replaced by then.
 *
 * `assignmentSluglinePattern` is the assignment's slugline apart from the
 * planning's, e.g. "TT-inrikes-vecko-pmv{week}". It applies
 * whatever section or variant the memo is written as; without it the
 * assignment takes the planning's slugline.
 *
 * An empty `titlePattern` or `slugline` opens those fields blank for the editor
 * to write, which the dialog requires before creating - the shape of the empty template.
 */
export const memoPlanningSchema = z.object({
  titlePattern: z.string(),
  slugline: z.string(),
  assignmentSluglinePattern: z.string().min(1).optional(),
  section: memoSectionNamingSchema.optional()
})

/**
 * A line rendered from the assignments a planning carries, one per assignment
 * of a type the entry names - "Text: lindintervju: Fullängdstext" for its text
 * assignment, "Bild: Partiledarintervju foto" for its picture one.
 *
 * `types` is what the entry covers, in precedence order: the lines are rendered
 * from the assignments of the first of them the item actually has, and the rest
 * only contribute their label. That is what makes a planning with both a
 * picture and a video assignment read "Bild/video: <the picture's title>" - the
 * video is worth mentioning, its own title is not worth printing twice.
 *
 * `labels` names each type, and `{assignmentLabel}` resolves to the labels of
 * the types the item has, distinct and joined with a slash. `{assignmentTitle}`
 * is the assignment's title; every other item placeholder is available too.
 *
 * An entry the item has no assignment for renders nothing - a memo only promises
 * the assignments a planning actually carries.
 */
export const memoAssignmentLineSchema = z.object({
  types: z.array(z.enum(assignmentTypes)).min(1),
  labels: z.partialRecord(z.enum(assignmentTypes), z.string().min(1)).default({}),
  pattern: z.string().min(1)
})

/**
 * Per item formatting. `headline` is the first line of an item, `lines` are
 * optional lines rendered below it, and `assignmentLines` are the lines that
 * come from the item's own assignments, rendered after them. A line whose
 * placeholders all resolve to empty is skipped entirely.
 *
 * `headlineRole` saves the headline as a `preamble`, for a memo about one item
 * whose headline is its ingress - the tillred. A memo keeps only its first one.
 */
export const memoItemFormatSchema = z.object({
  headline: z.string().min(1),
  headlineRole: z.enum(['preamble']).optional(),
  lines: z.array(z.string()).default([]),
  assignmentLines: z.array(memoAssignmentLineSchema).default([])
})

/**
 * What one section of a section grouped block is headed, e.g. "Inrikes" written
 * as "ÖVRIGT INRIKES". `section` is the section title as the datastore writes
 * it; it is matched trimmed and case insensitively, since the title comes from
 * there rather than from the template.
 *
 * The list is ordered, and that order is the order the sections are rendered in
 * - naming and order in one place rather than two that can drift apart.
 */
export const memoSectionHeadingSchema = z.object({
  section: z.string().min(1),
  heading: z.string().min(1)
})

/**
 * The items a block lists first under a subheading of their own, by source type
 * and newsvalue - the plannings at newsvalue 5 and 6 under "Toppnyheter". Highest
 * newsvalue first, then in the block's section order, with no section headings.
 */
export const memoTopNewsSchema = z.object({
  heading: z.string().min(1),
  types: z.array(memoSourceTypeSchema).min(1),
  newsvalues: z.array(memoNewsvalueSchema).min(1)
})

/**
 * A memo is one or more blocks, each rendering its own list of items. The
 * morning PM for instance has one block for the night's most important news
 * and one for the material to expect during the day. Every item is mapped into
 * the template's default block and is rendered by whichever block the editor
 * puts it in - see `defaultBlockId`.
 *
 * `preamble` is a block's own intro text. The block that ends up first (in
 * template order) with content has its preamble rendered as the memo's intro,
 * right after the title - so a memo whose first block never got any items
 * opens with whichever block actually did, and a memo with no content in any
 * block opens with no intro at all. Any later block's preamble is rendered above
 * its items as body text - the morning PM's "I dag kan du räkna med följande
 * material från TT:" - since a memo has one preamble. See `renderMemoLines`.
 *
 * `grouping` splits a block's items under subheadings: by the section they
 * belong to, or by the day they start on - the calendar listing a framtids-PM
 * is. A day grouped block names its subheading in `dayHeading`, which supports
 * the `{dayName}`, `{DayName}` and `{longDate}` placeholders, e.g. "{DayName}
 * den {longDate}" for "Torsdag den 27 augusti 2026". A section
 * grouped block orders its sections by first appearance among the selected
 * items, the same order the editor picked them in.
 *
 * `sectionHeadings` takes that over for a memo whose sections are written in a
 * fixed order under headings of their own - "ÖVRIGT INRIKES" for Inrikes, and
 * the sections in the order the list names them rather than the order the editor
 * happened to pick. A named section with no selected items is skipped.
 * `otherSectionHeading` is the heading everything the list does not name is
 * pooled under, last - sections the memo has no heading for, and the items that
 * carry no section at all. Without it those keep rendering the way they do in a
 * block that names no headings: each under its own section title, and the
 * sectionless ones unheaded at the end.
 *
 * `itemOrder` is the order the items of one rendered run come in - a section
 * within a day, a day, or the whole block. `column` is what every block does by
 * default: the material ahead of the events, each in the order the editor
 * arranged them. `time` sorts the run by the time of day its items happen at,
 * the ones with no time of day last, which is the order a calendar listing is
 * read in. A block whose event format prints `{time}` is ordered that way too.
 *
 * `keywords` lets the editor prefix each of the block's items with one of them,
 * picked per item in the creation dialog's preview and substituted for the
 * `{keyword}` placeholder. They are layout instructions for the print pipeline
 * rather than editorial text - "TOPP Rubriken" says the item goes at the top of
 * the spread - and are always optional per item. An empty list, the default,
 * means the block offers no picker at all.
 *
 * `eventItem` formats the block's events in place of `item`, for a block that
 * lists material and events differently - a time first, on one line.
 * `articleItem` does the same for the block's articles.
 *
 * `eventTitleSections` names the sections whose events print their title. An
 * event in any other section prints its description only, falling back on the
 * title when it has none. An empty list hides every event's title; leaving it
 * out prints them all. A title printed ahead of a description ends in a full
 * stop unless it already ends in punctuation or a symbol.
 *
 * `eventsHeading` is the lines rendered above the block's events, one entry.
 */
export const memoBlockSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  preamble: z.array(z.string()).default([]),
  heading: z.string().optional(),
  grouping: z.enum(['none', 'section', 'day']).default('none'),
  itemOrder: z.enum(['column', 'time']).default('column'),
  dayHeading: z.string().optional(),
  sectionHeadings: z.array(memoSectionHeadingSchema).default([]),
  otherSectionHeading: z.string().min(1).optional(),
  topNews: memoTopNewsSchema.optional(),
  keywords: z.array(z.string().min(1)).default([]),
  item: memoItemFormatSchema,
  eventItem: memoItemFormatSchema.optional(),
  articleItem: memoItemFormatSchema.optional(),
  eventTitleSections: z.array(z.string().min(1)).optional(),
  eventsHeading: z.array(z.string().min(1)).default([])
})

/**
 * One shape of a memo that is otherwise the same memo.
 *
 * The weekend PM is written on a Thursday for Friday through Sunday, or on a
 * Friday for Saturday through Monday. The candidates, the blocks and the
 * formatting are identical; the title, the slugline and the intro line are not,
 * which is what a variant carries. A template with variants takes those three
 * from the selected one rather than from `planning` and its intro block.
 *
 * `startWeekday` is the weekday the first covered day falls on, `0` for Sunday
 * the way `Date#getDay` counts, and it is what says which variant a memo is
 * written as: the creation dialog reads the variant off the first day the memo
 * is about, and answers the editor picking one by moving the days it covers onto
 * that variant's weekday. So the two can never disagree, no variant may share
 * its weekday with another, and a template with variants has to follow its span
 * - the guards on the template say so.
 */
export const memoVariantSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  startWeekday: z.number().int().min(0).max(6),
  titlePattern: z.string().min(1),
  slugline: z.string().min(1),
  preamble: z.array(z.string()).default([])
})

/**
 * The title placeholders that name more than the first day - the ones a
 * template following its span has to use for the span to be visible in what it
 * titles itself. `{weekday}`, `{date}` and `{dayName}` are deliberately not
 * among them: all three name the day the span starts on and read the same
 * whatever its end is. `{week}` reads off the first day too, but a week is a
 * span in itself - the one a vecko-PM's title names.
 */
const SPAN_PLACEHOLDERS = ['{dateRange}', '{shortDateRange}', '{fromDate}', '{toDate}', '{week}']

/**
 * The one placeholder a heading understands that a title does not, a title
 * being resolved in places where no type has been picked. Declared here because
 * the guards below are what keep it from being written into a template that
 * never asks for a type, where it could only ever resolve to nothing.
 */
export const EDITORIAL_INFO_TYPE = '{editorialInfoType}'

/** The chosen type's title without its "PM " prefix, e.g. "BNL" for "PM BNL". */
export const EDITORIAL_INFO_TYPE_NAME = '{editorialInfoTypeName}'

const EDITORIAL_INFO_TYPE_PLACEHOLDERS = [EDITORIAL_INFO_TYPE, EDITORIAL_INFO_TYPE_NAME]

/**
 * A heading used in place of `memoTitlePattern` when the chosen editorial-info
 * type is one of `types`. Matched on the type's title, trimmed and
 * case-insensitive, since the types come from the datastore with no stable key.
 */
export const memoEditorialInfoTypeHeadingSchema = z.object({
  types: z.array(z.string().min(1)).min(1),
  memoTitlePattern: z.string().min(1)
})

/**
 * Narrows the offered PMs to those whose title contains `title` as a phrase
 * (phrase prefix, case-insensitive). `latestOnly` offers just the newest match.
 */
export const memoInsertableDocumentsFilterSchema = z.object({
  title: z.string().trim().min(1),
  latestOnly: z.boolean().default(false)
})

function namesSpan(pattern: string): boolean {
  return SPAN_PLACEHOLDERS.some((placeholder) => pattern.includes(placeholder))
}

export function namesEditorialInfoType(pattern: string): boolean {
  return EDITORIAL_INFO_TYPE_PLACEHOLDERS.some((placeholder) => pattern.includes(placeholder))
}

/** What an editorial-info type's title is compared on. */
export function editorialInfoTypeKey(title: string): string {
  return title.trim().toLocaleLowerCase('sv-SE')
}

/**
 * `defaultSpanDays` is how many days past the first the creation dialog opens
 * its span on: `0`, the default, is the single day most memos are written
 * about, and a positive number pre-fills the second date that many days after
 * the first. Every template offers both dates either way - a memo is regularly
 * built out of two days' material, the morning PM reaching back a day for the
 * late news - so this says where the span opens and never whether there is one.
 * A template with leads per column opens every column's span this long.
 *
 * `titleFollowsSpan` says whether the titles and sluglines are resolved against
 * that span. They are not, normally: the span is what the candidates are drawn
 * from, and reaching either end of it out is a search rather than a statement
 * about which PM is being written, so a morning PM goes on being "TT:s
 * morgon-pm tisdagen 8 september" however far back it reaches. The dates the
 * titles do name are the day the memo belongs to plus this template's lead and
 * span - see `titleDate` on `useMemoDateRange`.
 *
 * A template whose title names a span the editor picks out rather than one that
 * follows from the day - the framtids-PM's "27.8-28.9", the vecko-PM's week -
 * turns this on and has its titles follow both dates. Naming no span in the
 * pattern is then a contradiction, and rejected below.
 *
 * `leadDays` is how far ahead of the days it covers a template is written: a
 * weekend PM published on the Thursday lists the Friday through the Sunday, so
 * it opens with its range a day ahead of the day the editor is on. The memo
 * itself still belongs to that day - the lead moves what is fetched and what the
 * title says, never where the planning is created. A source declaring a lead of
 * its own fetches by that one instead, see `leadDays` on the source.
 *
 * `spanStartWeekday` moves the day the span opens on forward to the next
 * occurrence of that weekday, `0` for Sunday the way `Date#getDay` counts, and
 * leaves it where it is when it already falls on one. That is for a template
 * given out on a fixed day of the week and covering the week that starts there:
 * a vecko-PM is a Monday's, so writing one on the Tuesday is writing next
 * Monday's rather than one covering Tuesday through Monday. It is applied after
 * `leadDays`, and only decides where the pickers open - both dates go on being
 * the editor's to move.
 *
 * `sectionChoice` decides where the memo is filed: `fixed` is every memo that
 * belongs in `MEMO_SECTION_TITLE`, `select` asks the editor for a section and
 * narrows the candidates to it. A template that is only sometimes written for
 * one section, which the editor narrows to one with the candidate filter or
 * leaves across the desk, stays `fixed` and says how it names itself either way
 * with `planning.section`.
 *
 * `suggest` also asks the editor where the memo is filed, but leaves the
 * candidates alone: the picker opens on the attached planning's section, else
 * the one section every picked item shares, else `MEMO_SECTION_TITLE`, and
 * stays on whatever the editor picks by hand until another planning is attached.
 *
 * `filingSection` pins a `fixed` template to one section by title, e.g.
 * "Utrikes", over both an attached planning's section and the section the
 * candidates are narrowed to. Without it the memo follows those, falling back
 * to `MEMO_SECTION_TITLE`.
 *
 * `requiresEditorialInfoType` gates a `links.core/editorial-info-type` picker in
 * the creation dialog: when true, the memo must be tagged with a type before it
 * can be created, since an untagged Print-PM is not funneled into the correct
 * publish pipe.
 *
 * `editorialInfoTypeMax` is how many types the picker takes. One create makes a
 * PM per picked type from the same preview, on one planning, each headed and
 * titled for its type: the print desk writes near-identical PMs for BNL, Gota
 * and NTM each day.
 *
 * `opensPlanning` makes "Skapa" open the planning the memo was filed in rather
 * than the memo itself - for a template whose PMs are read together there, as
 * the several a print PM create can make are.
 *
 * `dayGrouping` makes the day the memo's outer axis rather than a block's own:
 * every day the items fall on gets a heading of its own, named by `dayHeading`,
 * and each block is rendered within each day. That is the weekend PM's shape -
 * "FREDAG" and then that Friday's top news and material - as opposed to a block
 * that groups its own items by day, which is what `grouping: "day"` does.
 *
 * `defaultBlockId` names the block a candidate is mapped into on its way out of
 * the index, which is a placeholder rather than a destination: a picked
 * candidate waits on the batch bar for the block the editor says it belongs in
 * while more than one is active, and resolves into the one that is left when
 * only one is. It defaults to the first block, which is what most templates
 * want, but a template whose first block is one an editor opts an item *into*
 * names another one instead.
 *
 * `preamble` is the memo's own intro, for a memo whose opening line is one line
 * about the whole memo rather than the intro of whichever block leads. Without
 * it the intro is the leading block's preamble, which is what most templates
 * want - the morning PM opens differently depending on whether it leads with the
 * night's news or with the day's material. A template whose blocks are all opted
 * into cannot express a single opening line that way: the line would have to sit
 * on every block, and would then be repeated for each of them past the first.
 *
 * `insertableDocuments` offers the editor the PMs already published today,
 * listed in the preview, to fold into this one whole. It is opted into per template
 * because it only makes sense where a PM regularly restates one that went out
 * earlier - the morning PM the night's five - and would otherwise be a list of
 * the day's memos above every memo being written.
 * `insertableDocumentsFilter` narrows that list by title, e.g. to the latest
 * night's five only. An inserted PM is rendered
 * as content of the block the editor puts it in, which is why it cannot be
 * combined with `dayGrouping`: there every block is rendered once per day, so
 * the PM would be restated under each of them.
 * `insertableDocumentsBlock` names the block a published PM always goes into,
 * offered in that block's own header so nothing asks which block is meant.
 *
 * `insertsIntoEveryBlock` puts a picked candidate in all the blocks at once
 * rather than asking which one it belongs in. That is the print PM's shape: the
 * spread and the single page are largely written from the same material, and
 * which of the two a given item does not belong under is an editorial judgement
 * - so the memo starts with it in both and the editor takes out what does not
 * apply. It only says anything on a template with more than one block, and it
 * leaves nothing for a candidate to be mapped into and wait in, so
 * `defaultBlockId` may not accompany it.
 *
 * `requiresItems` is what makes a memo with nothing picked no memo at all,
 * which is true of all but one of them. A print PM is regularly written as a
 * headline and a body the editor types into the document afterwards - the
 * editorial info type it is tagged with is what it is *about* - so that one
 * template is created from an empty selection.
 *
 * `blankAfterTitle` saves one empty paragraph right below the memo's heading,
 * for a memo the editor types its body into after it is created.
 *
 * `singleItem` makes the memo about one post: picking another replaces the
 * pick and the columns offer no select-all - the tillred, written about one
 * late-announced event.
 *
 * `planningTitleMirrors` names the fields that copy the planning title as it is
 * typed. Picking an existing planning leaves them as they were; clearing it
 * resets them and the planning title to the template default. Typing in one of
 * them leaves the planning title alone. With a planning picked, a mirrored
 * `assignmentTitle` is what the other mirrors copy instead.
 *
 * `memoTitlePattern` is what the PM itself is headed with, as opposed to what
 * its planning item is called. Every memo's heading is a field in the preview,
 * so this only decides what that field opens holding: a template declaring none
 * opens with its `planning.titlePattern` over again, the planning and the PM
 * being the one thing named once until the editor says otherwise.
 *
 * Declaring one is for a template that needs the two to differ from the start.
 * It understands every placeholder `planning.titlePattern` does, and
 * `{editorialInfoType}` besides - the chosen type's title as written, which is
 * what a print PM heads itself with: the print desk writes a "PM BNL", where
 * the planning it is filed in is the day's print PM whichever type it is about.
 * A heading naming the type holds nothing until one is picked, so only a
 * template that asks for one may name it.
 *
 * `editorialInfoTypeHeadings` overrides `memoTitlePattern` for specific types,
 * e.g. the print PM for the regional pages. Types matching none fall back to it.
 *
 * `sectionOrder` lists section titles in the order a picked item is slotted
 * into its block by, matched trimmed and case insensitively. Sections it does
 * not name, and items without one, go last. Only the adding is sorted: the
 * editor is free to rearrange afterwards. An empty list appends as picked.
 */
export const memoTemplateSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  planning: memoPlanningSchema,
  sources: z.array(memoSourceSchema).min(1),
  preamble: z.array(z.string()).default([]),
  postamble: z.array(z.string()).default([]),
  defaultSpanDays: z.number().int().nonnegative().default(0),
  titleFollowsSpan: z.boolean().default(false),
  leadDays: z.number().int().nonnegative().default(0),
  spanStartWeekday: z.number().int().min(0).max(6).optional(),
  sectionChoice: z.enum(['fixed', 'select', 'suggest']).default('fixed'),
  filingSection: z.string().min(1).optional(),
  requiresEditorialInfoType: z.boolean().default(false),
  editorialInfoTypeMax: z.number().int().min(1).default(1),
  opensPlanning: z.boolean().default(false),
  dayGrouping: z.boolean().default(false),
  dayHeading: z.string().optional(),
  insertableDocuments: z.boolean().default(false),
  insertableDocumentsFilter: memoInsertableDocumentsFilterSchema.optional(),
  insertableDocumentsBlock: z.string().trim().min(1).optional(),
  insertsIntoEveryBlock: z.boolean().default(false),
  requiresItems: z.boolean().default(true),
  blankAfterTitle: z.boolean().default(false),
  singleItem: z.boolean().default(false),
  planningTitleMirrors: z.array(z.enum(['assignmentTitle', 'memoHeading'])).default([]),
  memoTitlePattern: z.string().min(1).optional(),
  editorialInfoTypeHeadings: z.array(memoEditorialInfoTypeHeadingSchema).default([]),
  defaultBlockId: z.string().optional(),
  sectionOrder: z.array(z.string().min(1)).default([]),
  variants: z.array(memoVariantSchema).default([]),
  blocks: z.array(memoBlockSchema).min(1)
}).superRefine((template, ctx) => {
  // Only the print article query knows what a flow is, so naming one anywhere
  // else is a template that quietly does not filter what it says it filters.
  for (const source of template.sources) {
    if (source.flow && source.type !== 'print-article') {
      ctx.addIssue({
        code: 'custom',
        path: ['sources'],
        message: `Source ${source.type} names a print flow, which only a print-article source has`
      })
    }
  }

  // A column has one range, so its sources cannot open it on different days.
  const columnLeads = new Map<string, number | undefined>()

  for (const source of template.sources) {
    const column = memoCandidateColumn(source.type)

    if (columnLeads.has(column) && columnLeads.get(column) !== source.leadDays) {
      ctx.addIssue({
        code: 'custom',
        path: ['sources'],
        message: `Sources in the ${column} column declare different leadDays`
      })
    }

    columnLeads.set(column, source.leadDays)
  }

  // Titles following the span, and so variants, need one span for the whole
  // memo, which per-column ranges do not have.
  if (template.titleFollowsSpan && hasColumnDates(template)) {
    ctx.addIssue({
      code: 'custom',
      path: ['sources'],
      message: 'Template gives its columns leads of their own but titles itself after its span'
    })
  }

  // A title that names no span cannot show one, so following the editor's span
  // with it is a flag that does nothing - the template would read as though its
  // titles moved with the dates while they never visibly did.
  if (template.titleFollowsSpan) {
    const patterns = [
      template.planning.titlePattern,
      ...(template.planning.section ? [template.planning.section.titlePattern] : []),
      ...template.variants.map((variant) => variant.titlePattern),
      ...(template.memoTitlePattern ? [template.memoTitlePattern] : []),
      ...template.editorialInfoTypeHeadings.map((heading) => heading.memoTitlePattern)
    ]
    const unspanned = patterns.filter((pattern) => !namesSpan(pattern))

    if (unspanned.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['titleFollowsSpan'],
        message: `Template follows its span but ${JSON.stringify(unspanned[0])} names none of`
          + ` ${SPAN_PLACEHOLDERS.join(', ')}`
      })
    }
  }

  // Both replace the title and the slugline wholesale, so a template carrying
  // the two of them has two namings for the same memo and no way to say which
  // of them a per-section variant would be written under.
  if (template.planning.section && template.variants.length) {
    ctx.addIssue({
      code: 'custom',
      path: ['planning', 'section'],
      message: 'Template names itself per section and also has variants, which name it too'
    })
  }

  // The picker decides where a `select` or `suggest` template is filed, so a
  // pinned section would be overridden by it or override it, and neither is
  // what it says.
  if (template.filingSection && template.sectionChoice !== 'fixed') {
    ctx.addIssue({
      code: 'custom',
      path: ['filingSection'],
      message: 'Template pins its filing section but also asks the editor for one'
    })
  }

  // Same reason as the per block guard below: days with no heading render as an
  // unheaded run, which reads as a template that simply does not group.
  if (template.dayGrouping && !template.dayHeading) {
    ctx.addIssue({
      code: 'custom',
      path: ['dayHeading'],
      message: 'Template groups by day but names no dayHeading'
    })
  }

  // An inserted PM belongs to one block, and a day grouped memo renders every
  // block once per day - so the PM would be restated under each day the memo
  // covers rather than once where the editor put it.
  if (template.insertableDocuments && template.dayGrouping) {
    ctx.addIssue({
      code: 'custom',
      path: ['insertableDocuments'],
      message: 'Template offers insertable documents but groups by day, which renders each block per day'
    })
  }

  // A filter on a list that is never offered narrows nothing.
  if (template.insertableDocumentsFilter && !template.insertableDocuments) {
    ctx.addIssue({
      code: 'custom',
      path: ['insertableDocumentsFilter'],
      message: 'Template filters insertable documents but offers none'
    })
  }

  // Same for a block nothing is ever inserted into.
  if (template.insertableDocumentsBlock && !template.insertableDocuments) {
    ctx.addIssue({
      code: 'custom',
      path: ['insertableDocumentsBlock'],
      message: 'Template names a block for insertable documents but offers none'
    })
  }

  // A typo here would leave the published PM with no header to be offered in.
  if (
    template.insertableDocumentsBlock
    && !template.blocks.some((block) => block.id === template.insertableDocumentsBlock)
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['insertableDocumentsBlock'],
      message: `No block is named ${template.insertableDocumentsBlock}`
    })
  }

  // Putting an item in every block is only ever an answer to which block it
  // belongs in, and a template with one block never asks - so the flag there
  // reads as a shape the template has while nothing about it differs.
  if (template.insertsIntoEveryBlock && template.blocks.length < 2) {
    ctx.addIssue({
      code: 'custom',
      path: ['insertsIntoEveryBlock'],
      message: 'Template inserts into every block but has only one, which is where its items go anyway'
    })
  }

  // The two say different things about the same pick: one block to hold it
  // while it waits on a choice, and every block at once with nothing to ask.
  if (template.insertsIntoEveryBlock && template.defaultBlockId) {
    ctx.addIssue({
      code: 'custom',
      path: ['defaultBlockId'],
      message: 'Template names a block candidates are mapped into and also inserts them into every block'
    })
  }

  // A PM per type needs a type picker to be asked at all.
  if (template.editorialInfoTypeMax > 1 && !template.requiresEditorialInfoType) {
    ctx.addIssue({
      code: 'custom',
      path: ['editorialInfoTypeMax'],
      message: 'Template takes several editorial-info types but asks for none'
    })
  }

  // A heading about a type nobody is ever asked for is a heading that resolves
  // to nothing, the placeholder being filled in from the picker the template
  // declined to put on screen.
  if (
    template.memoTitlePattern
    && namesEditorialInfoType(template.memoTitlePattern)
    && !template.requiresEditorialInfoType
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['memoTitlePattern'],
      message: `Template heads itself with ${EDITORIAL_INFO_TYPE} but asks for no editorial-info type`
    })
  }

  // Headings keyed on a type can only ever be picked from the type picker.
  if (template.editorialInfoTypeHeadings.length && !template.requiresEditorialInfoType) {
    ctx.addIssue({
      code: 'custom',
      path: ['editorialInfoTypeHeadings'],
      message: 'Template has per-type headings but asks for no editorial-info type'
    })
  }

  // A typed memo's assignment slugline is suffixed with its type, which a
  // fixed pattern would leave no room for.
  if (template.planning.assignmentSluglinePattern && template.requiresEditorialInfoType) {
    ctx.addIssue({
      code: 'custom',
      path: ['planning', 'assignmentSluglinePattern'],
      message: 'Template slugs its assignment by pattern but also by editorial-info type'
    })
  }

  // A type listed twice has two headings and no way to say which one wins.
  const seenTypes = new Set<string>()

  for (const heading of template.editorialInfoTypeHeadings) {
    for (const type of heading.types) {
      const key = editorialInfoTypeKey(type)

      if (seenTypes.has(key)) {
        ctx.addIssue({
          code: 'custom',
          path: ['editorialInfoTypeHeadings'],
          message: `Editorial-info type ${JSON.stringify(type)} has more than one heading`
        })
      }

      seenTypes.add(key)
    }
  }

  // Naming a block the template does not declare is a typo, and one nothing
  // downstream would report: a candidate mapped into a block that is not there
  // is resolved into a real one before it is ever rendered, so the template
  // would go on looking as though it had been read.
  if (
    template.defaultBlockId
    && !template.blocks.some((block) => block.id === template.defaultBlockId)
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['defaultBlockId'],
      message: `No block is named ${template.defaultBlockId}`
    })
  }

  // A section listed twice has two ranks, and only the first is ever read.
  const orderKeys = template.sectionOrder.map((section) => section.trim().toLocaleLowerCase())

  if (new Set(orderKeys).size !== orderKeys.length) {
    ctx.addIssue({
      code: 'custom',
      path: ['sectionOrder'],
      message: 'Template lists the same section twice in its sectionOrder'
    })
  }

  const variantIds = new Set(template.variants.map((variant) => variant.id))

  if (variantIds.size !== template.variants.length) {
    ctx.addIssue({
      code: 'custom',
      path: ['variants'],
      message: 'Two variants share an id'
    })
  }

  // The weekday is what says which variant a memo is written as: the creation
  // dialog reads it off the first day the memo is about, and answers a picked
  // variant by moving that day onto its weekday. Two variants sharing one is
  // then a variant nothing can select and nothing would ever show.
  const variantWeekdays = new Set(template.variants.map((variant) => variant.startWeekday))

  if (variantWeekdays.size !== template.variants.length) {
    ctx.addIssue({
      code: 'custom',
      path: ['variants'],
      message: 'Two variants start on the same weekday'
    })
  }

  // Same reason: the days the titles name are the days a variant is read from,
  // and for every template but one those follow the day the memo belongs to
  // rather than the span. Picking a variant would move the span, and the memo
  // would go on being titled - and shown in the picker - as the variant it was.
  if (template.variants.length && !template.titleFollowsSpan) {
    ctx.addIssue({
      code: 'custom',
      path: ['variants'],
      message: 'Template has variants but does not follow its span, which is what selects one'
    })
  }

  const drawsEvents = template.sources.some((source) => source.type === 'event')

  for (const block of template.blocks) {
    // Event formatting on a template that lists no events is never rendered.
    if (
      !drawsEvents
      && (block.eventItem || block.eventTitleSections || block.eventsHeading.length)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['blocks'],
        message: `Block ${block.id} formats events but the template draws none`
      })
    }

    // Without a pattern a day grouped block would render its days as an unheaded
    // run of items, which looks like a template that simply does not group.
    if (block.grouping === 'day' && !block.dayHeading) {
      ctx.addIssue({
        code: 'custom',
        path: ['blocks'],
        message: `Block ${block.id} groups by day but names no dayHeading`
      })
    }

    // Named section headings are read only where the items are actually split
    // by section, so naming them anywhere else is a block that quietly renders
    // none of them.
    if (
      block.grouping !== 'section'
      && (block.sectionHeadings.length || block.otherSectionHeading)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['blocks'],
        message: `Block ${block.id} names section headings but does not group by section`
      })
    }

    // The list is both the naming and the order, so a section named twice has
    // two places it could render and one of the two headings never shows.
    const sections = block.sectionHeadings.map(
      (entry) => entry.section.trim().toLocaleLowerCase()
    )

    if (new Set(sections).size !== sections.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['blocks'],
        message: `Block ${block.id} names the same section twice`
      })
    }

    // Without the placeholder the dialog would offer a keyword picker whose
    // choice never reaches the rendered memo.
    if (block.keywords.length && !block.item.headline.includes('{keyword}')) {
      ctx.addIssue({
        code: 'custom',
        path: ['blocks'],
        message: `Block ${block.id} offers keywords but its headline has no {keyword}`
      })
    }

    // A pattern that prints the label needs one for every type it covers -
    // otherwise the very item that carries the unlabelled type renders a line
    // that opens with a stray colon or nothing at all.
    for (const line of block.item.assignmentLines) {
      const unlabelled = line.types.filter((type) => !line.labels[type])

      if (line.pattern.includes('{assignmentLabel}') && unlabelled.length) {
        ctx.addIssue({
          code: 'custom',
          path: ['blocks'],
          message: `Block ${block.id} has an assignment line with no label for ${unlabelled.join(', ')}`
        })
      }
    }
  }
})

export type MemoSource = z.infer<typeof memoSourceSchema>
export type MemoSectionNaming = z.infer<typeof memoSectionNamingSchema>
export type MemoAssignmentLine = z.infer<typeof memoAssignmentLineSchema>
export type MemoSectionHeading = z.infer<typeof memoSectionHeadingSchema>
export type MemoTopNews = z.infer<typeof memoTopNewsSchema>
export type MemoVariant = z.infer<typeof memoVariantSchema>
export type MemoBlock = z.infer<typeof memoBlockSchema>
export type MemoTemplate = z.infer<typeof memoTemplateSchema>
export type MemoInsertableDocumentsFilter = z.infer<typeof memoInsertableDocumentsFilterSchema>
export type MemoSourceType = MemoSource['type']
export type MemoNewsvalue = MemoSource['newsvalues'][number]
